import type { DealCompMapEntry } from "./DealCompComparisonMap";
import type {
  DealCompsAnalysis,
  DealCompsPayload,
  DealCompsResponse,
  LeaseComp,
  LoanCompFromApi,
  SalesComp,
} from "./DealCompSectionTypes";

export function rowToDealCompMapEntry(
  row: Record<string, unknown>,
): DealCompMapEntry {
  const numOrNull = (v: unknown): number | null => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const strOrNull = (v: unknown): string | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === "string") return v.length ? v : null;
    return String(v);
  };

  const loc = strOrNull(row.location);
  const city = strOrNull(row.city);
  const location = loc?.trim() || city?.trim() || null;
  const assetType =
    strOrNull(row.asset_type) ?? strOrNull(row.area_type) ?? null;

  return {
    lat: numOrNull(row.lat),
    lng: numOrNull(row.lng),
    location,
    city,
    lender: strOrNull(row.lender),
    borrower: strOrNull(row.borrower),
    loan_amount: numOrNull(row.loan_amount),
    buyer: strOrNull(row.buyer),
    vendor: strOrNull(row.vendor),
    currency: strOrNull(row.currency),
    area_type: strOrNull(row.area_type),
    asset_type: assetType,
    yield_pct: numOrNull(row.yield_pct),
    sales_price: numOrNull(row.sales_price),
    no_of_keys_or_rooms: numOrNull(row.no_of_keys_or_rooms),
    price_per_area: numOrNull(row.price_per_area),
    price_per_key_room: numOrNull(row.price_per_key_room),
    tenant_name: strOrNull(row.tenant_name),
    asset_name: strOrNull(row.asset_name),
    lease_term: strOrNull(row.lease_term),
    total_rent: numOrNull(row.total_rent),
    /** Include on map when checked; API omits `checked` → treat as true. */
    checked: row.checked !== false,
    source: strOrNull(row.source),
    web_url: strOrNull(row.web_url),
    deal_name: strOrNull(row.deal_name),
    rent_per_area: numOrNull(row.rent_per_area),
    age_refurb_date: strOrNull(row.age_refurb_date),
  };
}

/** Explicit `false` only; missing/null/undefined from API → included (`true`). */
function normalizeCompRowChecked<T extends { checked?: boolean }>(row: T): T {
  return { ...row, checked: row.checked === false ? false : true };
}

export function normalizeLeaseComps(rows: LeaseComp[]): LeaseComp[] {
  return rows.map(normalizeCompRowChecked);
}

export function normalizeLoanComps(rows: LoanCompFromApi[]): LoanCompFromApi[] {
  return rows.map(normalizeCompRowChecked);
}

export function normalizeSalesComps(rows: SalesComp[]): SalesComp[] {
  return rows.map(normalizeCompRowChecked);
}

function analysisNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Reads `comps_analysis` from the comps GET envelope (sibling of `data`). */
export function resolveCompsAnalysisFromApiData(
  apiData: unknown,
): DealCompsAnalysis | null {
  if (apiData == null || typeof apiData !== "object") return null;
  const rec = apiData as Record<string, unknown>;
  const raw = rec.comps_analysis;
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    comps_avg_leases_rent_per_area: analysisNumber(
      o.comps_avg_leases_rent_per_area,
    ),
    comps_avg_sales_yield_pct: analysisNumber(o.comps_avg_sales_yield_pct),
    comps_avg_loans_margin: analysisNumber(o.comps_avg_loans_margin),
    comps_avg_loans_ltv: analysisNumber(o.comps_avg_loans_ltv),
    deal_leases_rent_per_area: analysisNumber(o.deal_leases_rent_per_area),
    deal_sales_yield_pct: analysisNumber(o.deal_sales_yield_pct),
    deal_loans_margin: analysisNumber(o.deal_loans_margin),
    deal_loans_ltv: analysisNumber(o.deal_loans_ltv),
    difference_leases_rent_per_area: analysisNumber(
      o.difference_leases_rent_per_area,
    ),
    difference_sales_yield_pct: analysisNumber(o.difference_sales_yield_pct),
    difference_loans_margin: analysisNumber(o.difference_loans_margin),
    difference_loans_ltv: analysisNumber(o.difference_loans_ltv),
    comps_avg_price_per_area: analysisNumber(o.comps_avg_price_per_area),
    deal_price_per_area: analysisNumber(o.deal_price_per_area),
    difference_price_per_area_pct: analysisNumber(
      o.difference_price_per_area_pct,
    ),
  };
}

export function resolveDealCompsPayloadFromApiData(
  apiData: unknown,
): DealCompsPayload | null {
  if (apiData == null) return null;
  if (typeof apiData === "object" && apiData !== null && "data" in apiData) {
    return (apiData as DealCompsResponse).data;
  }
  if (
    Array.isArray(apiData) &&
    apiData.length > 0 &&
    typeof apiData[0] === "object" &&
    apiData[0] !== null &&
    "data" in apiData[0]
  ) {
    return (apiData[0] as DealCompsResponse).data;
  }
  return apiData as DealCompsPayload;
}

/**
 * Moves rows with `checked: true` to the bottom with `isMoved: true`.
 * Uses row data (after tab snapshots) so it works when other tabs’ grids are unmounted.
 */
export function reorderSelectedToBottom<
  T extends { isMoved?: boolean; checked?: boolean },
>(rows: T[]): T[] {
  const unselected = rows
    .filter((row) => !row.checked)
    .map((row) => ({ ...row, checked: false }));
  const selected = rows
    .filter((row) => !!row.checked)
    .map((row) => ({
      ...row,
      isMoved: true,
      checked: true,
    }));
  return [...selected, ...unselected];
}

export function buildCompMapEntriesForTab(
  tab: number,
  leaseData: LeaseComp[],
  salesData: SalesComp[],
  loanData: LoanCompFromApi[],
): DealCompMapEntry[] {
  const rows: Record<string, unknown>[] =
    tab === 0
      ? (leaseData as unknown as Record<string, unknown>[])
      : tab === 1
        ? (salesData as unknown as Record<string, unknown>[])
        : (loanData as unknown as Record<string, unknown>[]);
  return rows.map((row) => rowToDealCompMapEntry(row));
}

export function collectSortedUniqueSources(data: DealCompsPayload): string[] {
  const sourceNamesLease = data?.lease_comps?.map(
    (comp: LeaseComp) => comp.source,
  );
  const sourceNamesLoan = data?.loan_comps?.map(
    (comp: LoanCompFromApi) => comp.source,
  );
  const sourceNamesSales = data?.sales_comps?.map(
    (comp: SalesComp) => comp.source,
  );
  return [
    ...new Set([...sourceNamesLease, ...sourceNamesLoan, ...sourceNamesSales]),
  ].sort() as string[];
}
